import { AtRisk, CommentIcon, FileSvgIcon } from '@/assets/images';
import { CustomShipmentText } from '@/components';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Divider, Flex, Timeline } from 'antd';
import React from 'react';
import CoCReplies, { Reply } from '../partials/CoCReplies';
import TimelineItemSharedPage from '../partials/TimelineItemSharedPage';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

import { CoCEventType } from '@/common/enum';
import { SharePageContentProps } from './types';

const SharedMapCOC: React.FC<SharePageContentProps> = ({
  shipment,
  halfHeight,
  setHalfHeightDirect,
  downloadFile,
  showFull,
  setShowFull,
  setHalfHeight,
  getFormattedTime,
  filteredChainOfCustody,
}) => {
  const atRiskSize = 30;
  const criticalDotSize = 36;
  const baseDotOffsetLeft = -11;
  const atRiskOffsetLeft = -25;
  const criticalOffsetLeft = -24;
  const commentOffsetLeft = -16;

  const setHalfHeightUnified = (id: string | number, height: number) => {
    if (setHalfHeightDirect) return setHalfHeightDirect(id, height);
    if (setHalfHeight) setHalfHeight((prev) => ({ ...prev, [id]: height }));
  };
  return (
    <div>
      {filteredChainOfCustody.length > 0 && (
        <>
          <Flex
            justify='space-between'
            align='center'
            gap={16}
            className='mt-8'
          >
            <CustomShipmentText
              text={themeT('chainOfCustody', theme.themeType)}
              fontSize='14px'
              fontWeight={600}
              lineHeight='28px'
            />
          </Flex>

          <div className='custom-timeline'>
            <Timeline mode='left'>
              {filteredChainOfCustody.map((custody, index) => {
                const isFirst = index === 0;
                const isLast = index === filteredChainOfCustody.length - 1;

                return (
                  <Timeline.Item
                    key={String(custody.id)}
                    dot={
                      custody?.type === CoCEventType.Comment ? (
                        <span
                          className='tl-dot tl-dot--white'
                          style={{
                            left: `${commentOffsetLeft}px`,
                            top: (halfHeight[custody.id] ?? 0) + 12,
                          }}
                        >
                          <img
                            src={CommentIcon}
                            style={{ width: 20, height: 20 }}
                          />
                        </span>
                      ) : custody?.type === CoCEventType.Alert &&
                        custody?.is_critical === 0 ? (
                        <span
                          className='tl-dot'
                          style={{
                            left: `${atRiskOffsetLeft}px`,
                            top: (halfHeight[custody.id] ?? 0) + 16,
                          }}
                        >
                          <div
                            className=''
                            style={{
                              width: criticalDotSize,
                              height: criticalDotSize,
                            }}
                          >
                            <img
                              src={AtRisk}
                              width={atRiskSize}
                              height={atRiskSize}
                            />
                          </div>
                        </span>
                      ) : custody?.type === CoCEventType.Alert &&
                        custody?.is_critical === 1 ? (
                        <span
                          className='tl-dot'
                          style={{
                            left: `${criticalOffsetLeft}px`,
                            top: (halfHeight[custody.id] ?? 0) + 16,
                          }}
                        >
                          <div
                            className='tl-dot__critical'
                            style={{
                              width: criticalDotSize,
                              height: criticalDotSize,
                            }}
                          >
                            <span className='tl-dot__critical-icon'>
                              <ExclamationCircleFilled />
                            </span>
                          </div>
                        </span>
                      ) : custody?.file &&
                        custody?.type === CoCEventType.FileUpload ? (
                        <span
                          className='tl-dot tl-dot--white'
                          style={{
                            left: '-12px',
                            top: (halfHeight[custody.id] ?? 0) + 12,
                          }}
                        >
                          <img width={14} src={FileSvgIcon} />
                        </span>
                      ) : (
                        <span
                          className='tl-dot'
                          style={{
                            left: `${baseDotOffsetLeft}px`,
                            top: (halfHeight[custody.id] ?? 0) + 16,
                          }}
                        >
                          <div className='tl-dot--black' />
                        </span>
                      )
                    }
                  >
                    {isFirst && (
                      <div
                        className='hide-tail start-tail'
                        style={{ height: (halfHeight[custody.id] ?? 0) + 16 }}
                      />
                    )}
                    {isLast && (
                      <div
                        className='hide-tail last-tail'
                        style={{
                          height: `calc(100% - ${
                            (halfHeight[custody.id] ?? 0) + 16
                          }px)`,
                        }}
                      />
                    )}

                    <TimelineItemSharedPage
                      isSharePage={true}
                      custody={custody}
                      setShowFull={setShowFull}
                      showFull={showFull}
                      getFormattedTime={getFormattedTime}
                      downloadFile={downloadFile}
                      setHalfHeight={setHalfHeightUnified}
                      shipment={shipment}
                    />

                    {(custody?.replies?.length ?? 0) > 0 && (
                      <>
                        {custody?.replies?.length > 0 && (
                          <Divider
                            titlePlacement='start'
                            plain
                            orientationMargin={0.1}
                            style={{
                              fontSize: '12px',
                              color: 'var(--text-secondary)',
                              width: '90%',
                            }}
                          >
                            {custody?.replies?.length}{' '}
                            {custody?.replies?.length > 1
                              ? 'replies '
                              : 'reply '}
                            {custody?.hidden_replies_count > 0 &&
                              '(some hidden)'}
                          </Divider>
                        )}
                        {custody?.replies?.map((item: Reply) => (
                          <CoCReplies
                            key={item?.id}
                            custody={{ ...custody, replies: [item] }}
                            downloadFile={downloadFile}
                            getFormattedTime={getFormattedTime}
                            showFull={showFull}
                            setShowFull={setShowFull}
                          />
                        ))}
                      </>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </div>
        </>
      )}
    </div>
  );
};

export default SharedMapCOC;
